import { List, Map } from 'immutable';
import * as React from 'react';

import * as Geographies from '../../constants/geographies';
import { Service } from '../../models/service';
import {
    LiveSiteEventSeverity,
    StatusPerServiceDocument,
    StatusPerServiceGeographyDocument,
} from '../../models/status';
import { getTitleForGeography } from '../../utilities/geographies';
import { FluentSvgIcon } from '../shared/FluentSvgIcon';
import { StatusIndicator } from '../shared/StatusIndicator';
import { getIconNameForService } from '../../utilities/services';

interface StatusMatrix {
    services: Map<string, Service>;
    statuses: List<StatusPerServiceDocument>;
}

interface ServiceTitleCellProps {
    id: string;
    name: string;
}

interface ServiceToRegionStatus {
    [serviceId: string]: {
        [geographyId: string]: StatusPerServiceGeographyDocument;
    };
}

function ServiceTitleCell(props: ServiceTitleCellProps) {
    const { id, name } = props;

    let nameJsx = <span>{name}</span>;

    if (id === 'IdentitySignIn') {
        nameJsx = (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                <span>Identity/</span>
                <span>Sign-In</span>
            </div>
        );
    }

    return (
        <td className="status-matrix__service-header">
            <div>
                <FluentSvgIcon className="status-matrix__header-icon" iconName={getIconNameForService(id)} />
            </div>

            {nameJsx}
        </td>
    );
}

/**
 * List the current status of each service in a matrix style with a column per geography and row per service.
 * @param props
 */
export const StatusMatrix = (props: StatusMatrix) => {
    const { services, statuses } = props;
    const rows = [...services.values()].sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
        }

        if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
        }

        return 0;
    });

    // prep the data to avoid looking through multiple times
    const serviceToRegionStatus: ServiceToRegionStatus = {};
    statuses.forEach((status) => {
        // TODO: Refactor name on this model to be id since it is the pascal-case value.
        const serviceId = status.name;

        // create an entry this service.
        serviceToRegionStatus[serviceId] = {};

        // create the corresponding entries for each of the geographies for a quick lookup
        (status.geographies || []).forEach((value) => {
            serviceToRegionStatus[serviceId][value.geography] = value;
        });
    });

    // put the geographies in order here
    const geographiesInOrder = [
        {
            key: Geographies.Americas,
            display: getTitleForGeography(Geographies.Americas),
        },
        {
            key: Geographies.Europe,
            display: getTitleForGeography(Geographies.Europe),
        },
        {
            key: Geographies.AsiaPacific,
            display: getTitleForGeography(Geographies.AsiaPacific),
        },
        {
            key: Geographies.MiddleEastAndAfrica,
            display: getTitleForGeography(Geographies.MiddleEastAndAfrica),
        },
    ];

    return (
        <div className="status-matrix-wrapper">
            <table aria-label="Visual Studio Service Status" cellSpacing={0} className="status-matrix">
                <thead>
                    <tr>
                        <th className="string-header">Service</th>
                        {geographiesInOrder.map((geo, geoIndex) => (
                            <th className="string-header string-header--geo" key={geoIndex}>
                                {geo.display}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows
                        .filter((svc) => svc.areActionsEnabled)
                        .map((svc, rowIndex) => (
                            <tr className="status-matrix__status-row" key={rowIndex}>
                                <ServiceTitleCell id={svc.id} name={svc.name} />

                                {geographiesInOrder.map((geo, geoIndex) => {
                                    const geographyStatuses = serviceToRegionStatus[svc.id];

                                    const geographyStatus = geographyStatuses[geo.key];
                                    const isAvailable = geographyStatus !== undefined;
                                    const severity = isAvailable
                                        ? (geographyStatus.severity as LiveSiteEventSeverity)
                                        : undefined;
                                    const state = isAvailable ? geographyStatus.state : undefined;

                                    return (
                                        <td key={`${rowIndex}+${geoIndex}`}>
                                            <StatusIndicator region={geo.display} severity={severity} state={state} />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
};
